import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, Sparkles, Zap, MessageCircle, CheckCircle, Award, Heart, Star, TrendingUp, Eye } from 'lucide-react';

const AboutUs = () => {
  return (
    <div className="bg-white min-h-screen font-sans">
      {/* Hero Section */}
      <div className="bg-exiser-primary text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-5xl md:text-7xl font-bold uppercase tracking-tight mb-6">
            About <span className="text-exiser-accent">Us</span>
          </h1>
          <p className="text-xl md:text-2xl text-purple-200 max-w-3xl mx-auto leading-relaxed font-light">
            Growth is not a struggle — it is a science.
          </p>
        </div>
      </div>

      {/* Intro Section */}
      <div className="py-20 max-w-4xl mx-auto px-4 text-center">
        <p className="text-2xl text-gray-800 font-light leading-relaxed mb-8">
          At Exiser, we believe growth is not a struggle — it is a science.
          A science that begins in the mind, expands through the right skills, and transforms into extraordinary results.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          We are a modern human-potential training company dedicated to empowering individuals, sales professionals, entrepreneurs, and organizations to grow creatively, confidently, and exponentially. Our approach blends practical skills, psychology, neuroscience, modern coaching, and spiritual intelligence, giving people a powerful system to rise beyond limitations and create meaningful, measurable success.
        </p>
      </div>

      {/* Who We Are */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold uppercase text-gray-900 mb-6 border-l-8 border-exiser-accent pl-6">Who We Are</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Exiser was founded with a simple but powerful vision:
                to help people unlock the highest version of themselves through structured growth, mindset transformation, and scientific personal development.
              </p>
              <p className="text-lg text-gray-700 mb-6 font-bold">
                We bring together world-class frameworks from:
              </p>
              <ul className="space-y-3">
                {[
                  "Human Behaviour & Psychology",
                  "Visualization Engineering & Law of Attraction",
                  "Sales Mastery & Communication Science",
                  "Scientific Meditation & Emotional Intelligence",
                  "Neuro-Linguistic Programming (NLP)"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-exiser-primary flex-shrink-0" />
                    <span className="text-gray-800">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-gray-600 italic">
                Every element of our training is designed not just to inspire — but to transform thinking, shift identity, and create long-lasting performance breakthroughs.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Team Collaboration"
                className="rounded-xl shadow-2xl relative z-10"
              />
              <div className="absolute -bottom-6 -right-6 bg-exiser-accent p-6 rounded-xl shadow-lg hidden md:block z-20">
                <p className="text-exiser-primary font-bold uppercase text-sm tracking-wider">Transforming Thinking</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What We Do */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold uppercase text-gray-900">What We Do</h2>
            <div className="w-24 h-1 bg-exiser-accent mx-auto mt-4 mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver high-impact training, coaching, and growth systems that help people:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Brain, title: "Develop a limitless mindset", desc: "Rewire your brain for success." },
              { icon: MessageCircle, title: "Master sales confidence", desc: "Communicate with authority." },
              { icon: Eye, title: "Build clarity", desc: "Through visualization and goal science." },
              { icon: Zap, title: "Activate focus and energy", desc: "With scientific exercises & meditation." },
              { icon: Heart, title: "Rewire emotional patterns", desc: "Increase resilience and strength." },
              { icon: Sparkles, title: "Create abundance", desc: "Advanced Law of Attraction & neuroscience." },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 p-8 rounded-lg border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1 group">
                <item.icon className="w-12 h-12 text-exiser-primary mb-4 group-hover:text-exiser-accent transition-colors" />
                <h3 className="text-xl font-bold uppercase text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 font-sans">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center max-w-4xl mx-auto bg-purple-50 p-8 rounded-2xl border border-purple-100">
            <p className="text-lg text-exiser-primary font-bold leading-relaxed">
              Our methods are practical, experiential, and results-driven. We focus on actionable learning, real-life transformation, and measurable progress — helping people shift from potential to performance and from dreams to achievements.
            </p>
          </div>
        </div>
      </div>

      {/* Why We Exist */}
      <div className="bg-exiser-dark text-white py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-exiser-primary rounded-full mix-blend-screen opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-exiser-accent rounded-full mix-blend-screen opacity-10 blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm border border-white/10">
                  <h4 className="text-exiser-accent font-bold text-xl mb-2">Think Better</h4>
                  <p className="text-sm text-gray-300">Clarity creates speed.</p>
                </div>
                <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm border border-white/10 mt-8">
                  <h4 className="text-exiser-accent font-bold text-xl mb-2">Feel Stronger</h4>
                  <p className="text-sm text-gray-300">Emotional mastery.</p>
                </div>
                <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm border border-white/10">
                  <h4 className="text-exiser-accent font-bold text-xl mb-2">Work Smarter</h4>
                  <p className="text-sm text-gray-300">Strategic action.</p>
                </div>
                <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm border border-white/10 mt-8">
                  <h4 className="text-exiser-accent font-bold text-xl mb-2">Grow Faster</h4>
                  <p className="text-sm text-gray-300">Exponential results.</p>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6 leading-tight">Why We <span className="text-exiser-accent">Exist</span></h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Because every person has greatness inside them. They only need the right guidance, tools, and environment to activate it.
              </p>
              <p className="text-lg text-gray-400 mb-8">
                We exist to help people live a life aligned with purpose, passion, and abundance.
              </p>
              <div className="inline-block border-l-4 border-exiser-accent pl-4 py-2">
                <p className="text-2xl font-bold text-white italic">
                  "At Exiser, we don’t just teach growth — we engineer it."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Promise */}
      <div className="bg-white py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold uppercase text-gray-900 mb-12">Our Promise To You</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4 text-left">
              <Star className="w-8 h-8 text-exiser-primary flex-shrink-0" />
              <div>
                <h4 className="font-bold uppercase text-lg text-gray-900">Authentic Transformation</h4>
                <p className="text-gray-600 font-sans">Not temporary motivation.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 text-left">
              <Brain className="w-8 h-8 text-exiser-primary flex-shrink-0" />
              <div>
                <h4 className="font-bold uppercase text-lg text-gray-900">Science-Backed Frameworks</h4>
                <p className="text-gray-600 font-sans">Not random theories.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 text-left">
              <TrendingUp className="w-8 h-8 text-exiser-primary flex-shrink-0" />
              <div>
                <h4 className="font-bold uppercase text-lg text-gray-900">Practical Tools</h4>
                <p className="text-gray-600 font-sans">Not complicated concepts.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 text-left">
              <Award className="w-8 h-8 text-exiser-primary flex-shrink-0" />
              <div>
                <h4 className="font-bold uppercase text-lg text-gray-900">Real Growth</h4>
                <p className="text-gray-600 font-sans">Personally and professionally.</p>
              </div>
            </div>
          </div>

          <div className="mt-16">
            <p className="text-gray-600 font-sans text-lg leading-relaxed mb-8">
              We are here to help you become the creator of your life, the master of your mind, and the leader of your destiny.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-exiser-primary text-white px-10 py-4 font-bold uppercase tracking-wider rounded shadow-xl hover:bg-purple-900 transition-transform hover:-translate-y-1">
              Start Your Journey
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;